import { rgb, RGB, PDFFont, PDFDocument, pushGraphicsState, concatTransformationMatrix, PDFName } from "pdf-lib";
import getPdfMake from "react-lib/appcon/common/pdfMake";

// Sapiens Header
import {
  AddHeaderSapiensPrintListStatic,
  AddHeaderSapiensPrintListStaticAddress,
  AddHeaderSapiensPrintListStaticMini,
  HeaderPrintListProps,
} from "./AddHeaderSapiensPrintList";

/**
 * Draw a texts on Static PDF (Example: FormConsentSpinalTH)
 * @param page - Page to draw
 * @param textCoordinates - Array of texts
 * @param font - Font name
 * @param fontSize - Font size
 * @param textColor - (Optional) Font color
 */
export const drawTextOnPage = (
  page: any,
  textCoordinates: { text: string | undefined; x: number; y: number }[],
  font: any,
  fontSize: number,
  textColor?: RGB
) => {
  for (const { text, x, y } of textCoordinates) {
    page.drawText(text || "", {
      x,
      y,
      size: fontSize,
      font,
      color: textColor ?? rgb(0, 0, 0),
    });
  }
};

/**
 * Calculate Center of Text (จัดกึ่งกลางให้กับ Text โดยอ้างอิงตาม Position X)
 * @param text - Text
 * @param font - Font Using
 * @param fontSize - Font Size
 * @param x1 - Position of X1
 * @param x2 - Position of X2
 * @param staticWidth - Known Width Case
 * @returns
 */
export const calculateCenterX = (
  text: string,
  font: PDFFont,
  fontSize: number,
  x1: number,
  x2: number,
  staticWidth?: number
) => {
  const textWidth = font.widthOfTextAtSize(text, fontSize);
  const targetWidth = staticWidth ?? x2 - x1;
  const centerX = x1 + (targetWidth - textWidth) / 2;
  return centerX;
};

/**
 * Truncate of Text (ตัด Text กรณีมีความยาวเกิน)
 * @param text - Text
 * @param font - Font Using
 * @param fontSize - Font Size
 * @param x1 - Position of X1
 * @param x2 - Position of X2
 * @param staticWidth - Known Width Case
 * @returns
 */
export const truncateText = (
  text: string,
  font: PDFFont,
  fontSize: number,
  x1: number,
  x2: number,
  staticWidth?: number
) => {
  let truncated = text;
  const ellipsis = "...";
  const ellipsisWidth = font.widthOfTextAtSize(ellipsis, fontSize);
  const maxWidth = staticWidth ?? x2 - x1;

  if (font.widthOfTextAtSize(text, fontSize) <= maxWidth) {
    return text;
  }

  while (font.widthOfTextAtSize(truncated, fontSize) + ellipsisWidth > maxWidth) {
    truncated = truncated.slice(0, -1);
  }

  return truncated + ellipsis;
};

/**
 * Generate PDFMake Header To Uint8Array
 * @param docDef - PDFMake Header DocDef (Ref. FormLoparoscopicTH)
 * @returns
 */
export const generateHeaderPdf = async (docDef: any): Promise<Uint8Array> => {
  const pdfMake = await getPdfMake(true);

  return new Promise((resolve) => {
    const doc = pdfMake.createPdf(docDef);
    doc.getBuffer((buffer: any) => {
      resolve(new Uint8Array(buffer));
    });
  });
};

/**
 * Merged PDFMake Header With Static PDF
 * @param staticPdfBytes - Static PDF (Uint8Array)
 * @param headerPdfBytes - PDFMake Header (Uint8Array) (Array Case - Ref. FormArthoscopicTH)
 * @returns
 */
export const mergedPdfHeader = async (
  staticPdfBytes: Uint8Array,
  headerPdfBytes: Uint8Array | Uint8Array[]
) => {
  const staticPdf = await PDFDocument.load(staticPdfBytes);
  const staticPages = staticPdf.getPages();

  if (Array.isArray(headerPdfBytes)) {
    const headerPdfs = await Promise.all(headerPdfBytes.map((bytes) => PDFDocument.load(bytes)));

    for (let i = 0; i < staticPages.length; i++) {
      const page = staticPages[i];

      // ถ้า header น้อยกว่าจำนวนหน้า PDF ใช้ตัวสุดท้ายเป็น default
      const headerPdf = headerPdfs[Math.min(i, headerPdfs.length - 1)];
      const headerPage = await staticPdf.embedPage(headerPdf.getPage(0));

      const { height } = page.getSize();
      const headerHeight = headerPage.height;

      page.drawPage(headerPage, {
        x: 0,
        y: height - headerHeight,
        width: headerPage.width,
        height: headerHeight,
      });
    }
  } else {
    const headerPdf = await PDFDocument.load(headerPdfBytes);
    const headerPage = await staticPdf.embedPage(headerPdf.getPage(0));
    const headerHeight = headerPage.height;

    for (const page of staticPages) {
      const { height } = page.getSize();

      page.drawPage(headerPage, {
        x: 0,
        y: height - headerHeight,
        width: headerPage.width,
        height: headerHeight,
      });
    }
  }

  return await staticPdf.save();
};

const getHeaderHeight = async (docDef: any): Promise<number> => {
  const pdfMake = await getPdfMake(true);
  const pdf = pdfMake.createPdf(docDef);

  return new Promise((resolve, reject) => {
    pdf.getBuffer((buffer: any) => {
      try {
        const tempDoc = (pdf as any).docDefinition;
        const [left, top, right, bottom] = tempDoc.pageMargins;
        resolve(top);
      } catch (error) {
        reject(error);
      }
    });
  });
};

/**
 * Shift PDF Content Down (เลื่อน content ของ static PDF ลงด้านล่าง) (Ref. FormConsentPDFPARamasri)
 * @param pdfBytes - Static PDF Bytes
 * @param shiftY - จำนวน point ที่ต้องการเลื่อนลง (เช่น 40 = ~2 บรรทัด)
 */
export const shiftPdfContentDown = async (
  pdfBytes: Uint8Array,
  shiftY: number
): Promise<Uint8Array> => {
  const pdfDoc = await PDFDocument.load(pdfBytes);
  const pages = pdfDoc.getPages();
  const contentsKey = PDFName.of('Contents');

  for (const page of pages) {
    const preRef = pdfDoc.context.register(
      pdfDoc.context.flateStream(`q 1 0 0 1 0 -${shiftY} cm\n`)
    );
    const postRef = pdfDoc.context.register(
      pdfDoc.context.flateStream(`Q\n`)
    );

    const existingRef = page.node.get(contentsKey);
    const newContents = pdfDoc.context.obj([preRef, existingRef, postRef]);
    page.node.set(contentsKey, newContents);
  }

  return await pdfDoc.save();
};

/**
 * Get PDFMake Header For Static PDF (Ref. FormLaparoscopicTH)
 * @param props - Heeader Info
 * @returns
 */
export const getHeaderStaticPdf = async (
  props: HeaderPrintListProps,
  options: "mini" | "address" | "normal" = "normal"
) => {
  let docDef: HeaderPrintListProps = {
    ...props,
  };

  const headerMap = {
    mini: AddHeaderSapiensPrintListStaticMini,
    address: AddHeaderSapiensPrintListStaticAddress,
    normal: AddHeaderSapiensPrintListStatic,
  };

  const headerUsage = headerMap[options];

  const originalDef = await headerUsage({}, docDef);

  const headerHeight = await getHeaderHeight(originalDef);

  return await headerUsage(
    {},
    {
      ...docDef,
      canvasHeight: headerHeight || 0,
    }
  );
};

/**
 * Fetch PDF bytes with fallback to default path
 * @param filePath - Primary file path (e.g. from headerInfo.file_path)
 * @param fallbackPath - Default PDF path to use if filePath is missing or fetch fails
 * @returns Uint8Array of PDF bytes
 */
export const fetchPdfWithFallback = async (
  filePath: string | undefined,
  fallbackPath: string
): Promise<Uint8Array> => {
  if (filePath) {
    try {
      const res = await fetch(filePath);
      if (!res.ok) throw new Error(`Failed to fetch: ${res.status}`);
      return new Uint8Array(await res.arrayBuffer() as ArrayBuffer);
    } catch {
      console.warn(`Falling back to default PDF: ${fallbackPath}`);
    }
  }
  return new Uint8Array(await fetch(fallbackPath).then((res) => res.arrayBuffer()) as ArrayBuffer);
};
